<template>
  <!-- prettier-ignore -->
  <div class="upload">
    <ovp-message
      v-if="item.message"
      class="upload__message"
      :text="item.message"
      severity="warning"
    />
    <div
      v-if="item.description"
      class="upload__description"
    >
      {{ item.description }}

      <ovp-icon
        v-tooltip="{ allowHTML: true, content: $t(`form.file.disclaimer`) }"
        art="light"
        name="circle-info"
        tabindex="-1"
      />
    </div>

    <file-pond
      allow-multiple
      :files="files"
      :max-files="maxFiles"
      :max-file-size="maxFileSize"
      :accepted-file-types="acceptedFileTypes"
      @update="upload"
      @error="handleError">
      <div class="upload__content">
        <template v-if="files.length < maxFiles">
          <ovp-icon
            class="upload__icon"
            art="light"
            name="upload"
          />

          <span v-html="$t('form.file.idle')"/>
        </template>
        <span v-else>{{ $t('error.MAX_FILES') }}</span>
      </div>
    </file-pond>

    <file-list
      :files="files"
      @remove="remove"
      @open="open"
    />
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import FileHandler, { STATUS } from '@/helpers/FileHandler'

import FilePond from '@/components/Form/File/FilePond'
import FileList from '@/components/Form/File/List'
import { SOURCE } from '@/config/events'
import { NOTIFICATION } from '@/config/constants'
import OvpMessage from '@/components/Container/OvpMessage'
import { useNotifications } from '@/hooks'
import { loading$ } from '@/services'

// Hooks
const { t } = useI18n()
const { addErrorNotification, addNotification } = useNotifications()

// Init
const emit = defineEmits(['open', 'remove', 'saveBasket', 'upload'])

const props = defineProps({
  personId: {
    type: String,
    default: null,
  },

  item: {
    type: Object,
    required: true,
  },

  existingFiles: {
    type: Array,
    required: true,
  },

  // the residence-permit controller expects the FE to set the fileId
  // FLEX-1256 the advisory protocol controller sets the fileId itself
  generateIdForNewFiles: {
    type: Boolean,
    default: false,
  },
})
//TODO file structure order
// Data
const maxFiles = 4
const maxFileSize = '5MB'
const acceptedFileTypes = 'image/jpeg,image/png,application/pdf'

const filters = {
  maxFiles,
  maxFileSize,
  acceptedFileTypes,
}

const files = ref([])

// methods
/**
 * Initialize files and add already uploaded files
 */
function initializeFiles() {
  files.value = []

  if (props.existingFiles && props.existingFiles.length) {
    addFiles(props.existingFiles)
  }
}

/**
 * @param {Object[]} __files
 */
function addFiles(__files) {
  const promises = __files.map(file => {
    const fileHandler = new FileHandler(file, { filters, generateIdForNewFiles: props.generateIdForNewFiles })
    files.value.push(fileHandler)
    return Promise.resolve(fileHandler)
  })

  return Promise.all(promises).then(() => files.value)
}

/**
 * @param {FileHandler[]} __files new file(s) to be uploaded
 */
function upload(__files) {
  if (__files.length + files.value.length > maxFiles) {
    handleError(files)
  }

  addFiles(__files).then(addedFiles => {
    const [validFiles, invalidFiles] = checkFiles(addedFiles)
    if (!validFiles.length && invalidFiles.length) {
      return showInvalidFileNotification()
    }

    return uploadProgress(validFiles, invalidFiles)
  })
}

/**
 * @param {FileHandler} file
 */
function open(file) {
  emit('open', file.id)
}

/**
 * @param {FileHandler} file
 */
function remove(file) {
  if (loading$.getState().active.value) {
    return
  }

  const fileId = file.id
  const removeFile = id => {
    files.value = files.value.filter(currentFile => currentFile.id !== id)
  }

  // If file not uploaded (READY) then delete it form list directly without MS call
  if (file.status !== STATUS.READY) {
    return removeFile(fileId)
  }

  emit('remove', fileId)
}

/**
 * Splits files into two separate arrays containing valid and invalid files
 */
function checkFiles(__files) {
  const isValid = file => file.status !== STATUS.ERROR
  return __files.reduce(
    ([valid, invalid], file) => {
      return isValid(file) ? [[...valid, file], invalid] : [valid, [...invalid, file]]
    },
    [[], []]
  )
}

function showInvalidFileNotification() {
  const message = t('form.file.removeInvalids')
  addNotification(message, NOTIFICATION.WARNING)
}

/**
 * @param {FileHandler[]} validFiles
 * @param {FileHandler[]} invalidFiles
 * @returns {Promise<FileHandler[]>}
 * @private
 */
function uploadProgress(validFiles, invalidFiles) {
  if (loading$.getState().active.value) {
    return Promise.reject()
  }

  loading$.start()

  return nextTick(() => {
    return uploadFiles(validFiles)
      .then(__files => {
        const count = __files?.filter(file => file?.status === STATUS.READY).length
        if (count > 0) {
          const message = t('form.file.uploaded', { count })
          addNotification(message)
        }
        if (invalidFiles.length) {
          showInvalidFileNotification()
        }
        // save basket. The uploaded documents will appear in the updated basket object.
        emit('saveBasket')
      })
      .finally(() => loading$.end())
  })
}

/**
 * @param {FileHandler[]} files
 * @returns {Promise<FileHandler[]>}
 * @private
 */
function uploadFiles(files) {
  const promises = files.map(file => {
    if (file.status !== STATUS.WAITING) {
      return
    }

    const config = {
      onUploadProgress: event => file.onUploadProgress(event),
    }

    // parent component handles the upload of the files => emit Promise to parent.
    return new Promise(resolve => {
      emit('upload', {
        fileId: file.id,
        fileData: file.getData(),
        config,
        onSuccess: response => file.onSuccess(response),
        onError: error => file.onError(error),
        resolve,
      })
    })
  })

  return Promise.all(promises)
}

function handleError(errorCode) {
  if (errorCode?.source !== SOURCE.BACKEND) {
    addErrorNotification({ errorCode })
  }
}

// Watchers
watch(
  () => props.personId,
  () => {
    initializeFiles()
  }
)

watch(
  () => props.existingFiles,
  () => {
    initializeFiles()
  }
)

// Lifecycle hooks
onMounted(() => {
  initializeFiles()
})
</script>

<style scoped lang="postcss">
.upload__message {
  margin-top: 10px;
}

.upload__description {
  margin-bottom: 10px;
}

.upload__content {
  display: flex;
  justify-content: center;
  align-items: center;
}

.upload__icon {
  font-size: 20px;
  color: var(--c-black);
  margin-right: 5px;
}
</style>
